<template>
  <div class="appointment-time-selector">
    <h2 class="title-with-underline">請選擇就診時間</h2>

    <div class="reminder-grayscale">
      說明：取得可預約日期後，顯示上一頁選擇的可預約日期，它之後可預約的看診時段
    </div>

    <div class="date-selection">
      <div
        v-for="(day, index) in filteredDates"
        :key="index"
        class="appointment-day"
      >
        <h4 class="date-title">{{ day.date }} ({{ getWeekday(day.date) }})</h4>

        <div class="time-slots-container">
          <button
            v-for="(item, index) in transformSlots(day.timeSlots, day.date)"
            :key="index"
            class="time-slot"
            :class="{
              full: item.isFull,
              selected: item.value === value
            }"
            @click="handleSelectionChange(item.value)"
          >
            {{ item.label }} {{ item.description }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'AppointmentTimeSelector',
  props: {
    dates: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: null
    },
    selectedDate: {
      type: String,
      default: null
    }
  },
  computed: {
    // Filter dates to show from selectedDate to 4 days after
    filteredDates() {
      if (!this.selectedDate) {
        return this.dates;
      }

      // Create a range of dates from selectedDate to 4 days after
      const selectedMoment = moment(this.selectedDate);
      const endDate = selectedMoment.clone().add(4, 'days');

      // Filter dates that fall within this range
      return this.dates.filter(day => {
        const dayMoment = moment(day.date);
        return dayMoment.isSameOrAfter(selectedMoment, 'day') &&
        dayMoment.isSameOrBefore(endDate, 'day');
      });
    }
  },
  watch: {
    // Watch for changes in selectedDate or value to validate the current selection
    selectedDate: {
      immediate: true,
      handler() {
        this.validateAndEmitIfNeeded();
      }
    },
    value: {
      immediate: true,
      handler() {
        this.validateAndEmitIfNeeded();
      }
    }
  },
  methods: {
    handleSelectionChange(selection) {
      this.$emit('input', selection);
    },

    getWeekday(dateString) {
      return ['日', '一', '二', '三', '四', '五', '六'][moment(dateString).day()];
    },

    validateAndEmitIfNeeded() {
      // If there's no value or no selectedDate, nothing to validate
      if (!this.value || !this.selectedDate) return;

      try {
        // Extract date from stored time slot (format: "YYYY-MM-DD-type")
        const timeSlotDateStr = this.value.split('-').slice(0, 3).join('-');

        // Parse dates for comparison
        const selectedMoment = moment(this.selectedDate);
        const timeSlotMoment = moment(timeSlotDateStr);
        const endDate = selectedMoment.clone().add(4, 'days');

        // If the stored time slot date is outside the displayed range, emit null
        if (timeSlotMoment.isBefore(selectedMoment, 'day') || timeSlotMoment.isAfter(endDate, 'day')) {
          console.log('Stored time slot is outside displayed range, clearing selection');
          this.$emit('input', null);
        }
      } catch (error) {
        // If there's any error parsing dates, emit null
        console.warn('Invalid time slot format, clearing selection:', error);
        this.$emit('input', null);
      }
    },

    transformSlots(timeSlots, date) {
      const typeMap = {
        morning: '上午診',
        afternoon: '下午診',
        evening: '夜診'
      };

      return timeSlots.map(slot => {
        const disabled = slot.available !== 'true';
        const isFull = !disabled && slot.bookedCount >= slot.bookMax;
        const timePeriod = typeMap[slot.type] || slot.type

        let description = '';
        if (disabled) {
          description = '';
        } else if (isFull) {
          description = '(滿)';
        } else {
          description = `(${slot.bookedCount})`;
        }

        return {
          label: timePeriod,
          value: `${date}-${slot.type}`,
          description,
          disabled,
          isFull
        };
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.appointment-day + .appointment-day {
  margin-top: 48px;
}

.date-title {
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.date-selection {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 32px 16px;
  margin-bottom: 24px;
}

.time-slots-container {
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 0;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media screen and (min-width: 768px) {
    gap: 16px;
  }
}

.time-slot {
  border: 2px solid #2a9d8f;
  border-radius: 10px;
  padding: 12px 8px;
  background: #fff;
  color: #207868;
  font-size: 14px;
  font-weight: 700;
  flex: 1;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  @media screen and (min-width: 768px) {
    padding: 14px 20px;
    font-size: 16px;
  }

  &:not(.full):hover {
    background: #2a9d8f;
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(42, 157, 143, 0.3);
  }

  &.selected {
    background: #207868;
    border-color: #207868;
    color: #fff;
  }

  &.full {
    background: #f8f9fa;
    color: #999;
    border-color: #e9ecef;
    font-weight: 500;
    cursor: not-allowed;
  }
}
</style>
